import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import type { QueryService } from "../QueryService";

interface UseAllQueryResultsOptions {
  /** Rows per request; the API caps a page at 1000. */
  pageSize?: number;
  /** Stop after this many rows and report `truncated`, so a huge result cannot exhaust the browser. */
  maxResults?: number;
}

export interface AllQueryResults<T> {
  items: T[];
  /** True when `maxResults` stopped the drain before the last page. */
  truncated: boolean;
}

/**
 * Query options for draining every page of a submitted query. Extracted from
 * the hook so code outside render can await the same rows through
 * `queryClient.fetchQuery`, sharing the hook's cache entry instead of
 * fetching twice.
 */
export function allQueryResultsOptions<T>(
  queryService: QueryService,
  queryId: string | undefined,
  { pageSize = 1000, maxResults = 5000 }: UseAllQueryResultsOptions = {},
) {
  return queryOptions({
    queryKey: ["queryResults", queryId, pageSize, maxResults, "all"],
    queryFn: async ({ signal }): Promise<AllQueryResults<T>> => {
      const items: T[] = [];
      let nextPageToken: string | undefined;
      do {
        const page = await queryService.getQueryResults<T>(queryId!, {
          abortSignal: signal,
          nextPageToken,
          pageSize,
        });
        items.push(...page.items);
        nextPageToken = page.next_token ?? undefined;
        if (items.length >= maxResults) {
          return {
            items: items.slice(0, maxResults),
            truncated: nextPageToken !== undefined || items.length > maxResults,
          };
        }
      } while (nextPageToken !== undefined);
      return { items, truncated: false };
    },
    enabled: queryId !== undefined,
    staleTime: Infinity,
  });
}

/**
 * Drain every page of a submitted query into one array. For views that need
 * the whole result set client-side (a timeline), unlike the paginated table
 * hooks. Pages are fetched serially; the query is cached for as long as the
 * `query_id` is. A new `queryId` keeps the previous rows,
 * flagged `isPlaceholderData`, until its own arrive, so a view can keep
 * showing what it had while its filters change.
 */
export function useAllQueryResults<T>(
  queryId: string | undefined,
  options: UseAllQueryResultsOptions = {},
): UseQueryResult<AllQueryResults<T>, Error> {
  const { queryService } = useDomainServices();

  return useQuery({
    ...allQueryResultsOptions<T>(queryService, queryId, options),
    placeholderData: keepPreviousData,
  });
}
